/**
 * Currency conversion primitives.
 *
 * The Travelix v2 MVP ships with a hard-coded snapshot of common
 * cross-rates, updated when the release ships. A future revision can
 * replace `RATES` with a runtime fetcher (Firestore doc, cloud
 * function) without changing this file's public surface — `convert()`
 * and `hasRate()` stay the same, and every call site continues to work.
 *
 * Reasons to keep it static for now:
 *   1. FX for group expenses only needs "roughly right" — the group
 *      settles the final tab through their own bank transfer, not us.
 *   2. Zero network calls on the expense flow keeps the offline
 *      experience unbroken.
 *   3. Rates change slowly enough that a per-release refresh is fine.
 *
 * The base currency for the internal table is EUR. `convert(from → to)`
 * routes through EUR: `amount * (1/rate[from]) * rate[to]`.
 */

// Snapshot as of Sep 2025. Source: ECB reference rates.
// eur is 1.0 by definition (the base of the table).
const RATES: Record<string, number> = {
  EUR: 1.0,
  USD: 1.08,
  GBP: 0.86,
  JPY: 158.0,
  CHF: 0.94,
  SEK: 11.35,
  NOK: 11.55,
  DKK: 7.46,
  CAD: 1.47,
  AUD: 1.64,
  NZD: 1.79,
  MXN: 21.5,
  BRL: 6.05,
  ARS: 1080.0,
  CLP: 1010.0,
  COP: 4400.0,
  THB: 38.5,
  INR: 90.5,
  IDR: 17200.0,
  MYR: 4.85,
  SGD: 1.45,
  HKD: 8.45,
  KRW: 1490.0,
  CNY: 7.85,
  TWD: 34.8,
  TRY: 36.8,
  ZAR: 20.15,
  AED: 3.97,
  ILS: 4.05,
  PLN: 4.28,
  CZK: 25.35,
  HUF: 400.0,
  RON: 4.97,
  ISK: 149.0,
  RUB: 100.0,
} as const;

/**
 * List of currencies with a known rate. Handy for a currency picker.
 */
export const SUPPORTED_CURRENCIES: readonly string[] =
  Object.keys(RATES).sort();

export function hasRate(code: string): boolean {
  return code in RATES;
}

/**
 * Convert `amount` from `fromCurrency` to `toCurrency`. Returns null when
 * either currency has no known rate — callers surface that as a UI hint
 * ("no rate for XYZ; enter the amount in the trip currency").
 *
 * The `from === to` path is a fast identity (no floating-point drift).
 */
export function convert(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
): number | null {
  const from = fromCurrency.toUpperCase();
  const to = toCurrency.toUpperCase();
  if (from === to) return round2(amount);
  const rFrom = RATES[from];
  const rTo = RATES[to];
  if (rFrom == null || rTo == null) return null;
  // amount in EUR = amount / rFrom; then × rTo to reach target.
  return round2((amount / rFrom) * rTo);
}

/**
 * The rate `to / from` — i.e. how many `to` units one `from` unit buys.
 * Convenience for the "≈ €48.00 at 0.93" caption under the amount input.
 */
export function rate(fromCurrency: string, toCurrency: string): number | null {
  const from = fromCurrency.toUpperCase();
  const to = toCurrency.toUpperCase();
  if (from === to) return 1;
  const rFrom = RATES[from];
  const rTo = RATES[to];
  if (rFrom == null || rTo == null) return null;
  return round4(rTo / rFrom);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

/**
 * Convert a merchant-charged amount into the trip currency for storage.
 *
 * `lockedRate` is the rate stored on an existing expense: editing an expense
 * without changing its currency must reuse that rate, not today's, or the
 * trip-currency amount (and everyone's balance) drifts on every edit.
 *
 * Returns null when no rate is known — callers must block the save rather
 * than fall back to the raw amount (¥10,000 is not €10,000).
 */
export function toTripCurrency(
  amount: number,
  fromCurrency: string,
  tripCurrency: string,
  lockedRate?: number | null,
): number | null {
  if (fromCurrency.toUpperCase() === tripCurrency.toUpperCase()) return round2(amount);
  if (lockedRate != null && lockedRate > 0) return round2(amount * lockedRate);
  return convert(amount, fromCurrency, tripCurrency);
}
